import { getToolRegistry } from '../tools/registry.js';
import { discoverSkills } from '../skills/index.js';
import { SLASH_COMMANDS } from './index.js';

export interface GuideSection {
  title: string;
  /** Body lines (already formatted, no leading bullets required). */
  lines: string[];
}

/**
 * How to reach Max beyond the terminal. Shown during first-run onboarding and in
 * the `/guide` output so users know the CLI isn't the only way in.
 */
export const CHANNEL_GUIDE: GuideSection = {
  title: 'Channels — chat with Max anywhere',
  lines: [
    '  Terminal — you are here.',
    '  WhatsApp — message Max from your phone:',
    '    1. Link your phone:   max gateway login   (scan the QR code)',
    '    2. Start the gateway: max gateway run',
    '    3. Message yourself on WhatsApp — Max replies in that chat.',
    '  (From source: "bun run gateway:login" then "bun run gateway".)',
  ],
};

/** Tool groupings for the guide, in display order. Names not listed fall into "More tools". */
const TOOL_GROUPS: { title: string; tools: string[] }[] = [
  {
    title: 'Indian-market research (Drishti)',
    tools: [
      'company_research',
      'peer_group_research',
      'portfolio_brief',
      'market_pulse',
      'earnings_analysis',
      'earnings_metric_comparison',
      'result_season_tracker',
      'price_volume_signal_explainer',
      'event_calendar',
      'concall_source_pack',
      'filing_source_pack',
      'orders_capex_tracker',
      'corporate_action_monitor',
      'governance_monitor',
      'risk_monitor',
      'get_financials',
      'get_market_data',
      'read_filings',
    ],
  },
  {
    title: 'Web & open research',
    tools: ['web_search', 'x_search', 'browser', 'web_fetch'],
  },
  {
    title: 'Files',
    tools: ['read_file', 'write_file', 'edit_file'],
  },
  {
    title: 'Memory',
    tools: ['memory_search', 'memory_get', 'memory_update'],
  },
  {
    title: 'Automation & delegation',
    tools: ['cron', 'heartbeat', 'spawn_subagent', 'ask_user_question'],
  },
];

/**
 * Build the "what can Max do" guide. Tools and skills are pulled live from the
 * registries so the guide stays accurate as capabilities are added or gated by
 * configuration (only tools available for the current model/keys are listed).
 */
export function buildGuideSections(model: string | null): GuideSection[] {
  const registry = getToolRegistry(model ?? '');
  const byName = new Map(registry.map((t) => [t.name, t]));
  const grouped = new Set<string>();
  const sections: GuideSection[] = [];

  sections.push({
    title: 'Max — what I can do',
    lines: [
      'I am a financial research agent that plans, calls tools for live data,',
      'checks my own work, and iterates to a data-backed answer. Just ask in plain',
      'English — I pick the right tools. Use the slash commands below to configure me.',
    ],
  });

  for (const group of TOOL_GROUPS) {
    const lines: string[] = [];
    for (const name of group.tools) {
      const tool = byName.get(name);
      if (!tool) continue; // not available (gated by config) — skip silently
      grouped.add(name);
      lines.push(`  ${name} — ${tool.compactDescription}`);
    }
    if (lines.length > 0) {
      sections.push({ title: group.title, lines });
    }
  }

  // Any registered tool not in a known group (e.g. newly added) — never drop it.
  const leftovers = registry.filter((t) => !grouped.has(t.name) && t.name !== 'skill');
  if (leftovers.length > 0) {
    sections.push({
      title: 'More tools',
      lines: leftovers.map((t) => `  ${t.name} — ${t.compactDescription}`),
    });
  }

  const skills = discoverSkills();
  if (skills.length > 0) {
    sections.push({
      title: 'Skills (specialized workflows)',
      lines: skills.map((s) => `  ${s.name} — ${s.description}`),
    });
  }

  sections.push(CHANNEL_GUIDE);

  // Derived from the single command registry so it never drifts.
  const cmdWidth = Math.max(...SLASH_COMMANDS.map((c) => c.name.length));
  sections.push({
    title: 'Commands',
    lines: SLASH_COMMANDS.map((c) => `  /${c.name.padEnd(cmdWidth)}  ${c.description}`),
  });

  sections.push({
    title: 'Try asking',
    lines: [
      '  "Give me a full research dossier on Reliance Industries"',
      '  "Compare Q3 revenue and margins for TCS, Infosys, and Wipro"',
      '  "What moved the Nifty today and why?"',
      '  "Summarize the latest earnings concall for HDFC Bank"',
      '  "Any governance or risk red flags at Adani Enterprises lately?"',
      '  "Write an investment memo on Tata Motors"',
    ],
  });

  return sections;
}
